// Components
import TextTitle from "./components/commercetools-ui/text-title";
import ProductAvailabilityTags from "./components/product-availability";
import ProductColorSwatches from "./components/product-color-swatches";
import ColorFacetSwatch from "./components/product-color-swatches/ColorFacetSwatch";
import SidebarSelectedFiltersComponent from "./components/sidebar-selected-filters/SidebarSelectedFilters";
import type { SidebarSelectedFiltersProps } from "./components/sidebar-selected-filters/SidebarSelectedFilters";
import { getProductColorOptions } from "./components/product-color-swatches/helpers";
import UpsTrackingLinkComponent from "./components/commercetools-ui/organisms/account/sections/order-detail-page/ups-tracking-link";
import type { UpsTrackingLinkProps } from "./components/commercetools-ui/organisms/account/sections/order-detail-page/ups-tracking-link";

// Atoms
import ToasterComponent from "./components/commercetools-ui/atoms/toaster";
import SkeletonComponent from "./components/commercetools-ui/atoms/skeleton";
import PDFDisplayComponent from "./components/commercetools-ui/atoms/pdf-viewer";
import SVGIconComponent from "./components/commercetools-ui/atoms/svg-icon";
import BreadcrumbComponent from "./components/commercetools-ui/atoms/breadcrumb";
import ButtonComponent from "./components/commercetools-ui/atoms/button";
import ButtonLoadingIcon from "./components/commercetools-ui/atoms/button/loadingIcon";
import ButtonFeedbackIconLayer from "./components/commercetools-ui/atoms/button/feedbackIconLayer";
import CustomTooltipComponent from "./components/commercetools-ui/atoms/tooltip";
import SubtitleComponent from "./components/commercetools-ui/atoms/subtitle";
import OverlayComponent from "./components/commercetools-ui/atoms/overlay";
import InfoComponent from "./components/commercetools-ui/atoms/info";
import SideMenuComponent from "./components/commercetools-ui/atoms/side-menu";
import CustomDropDownComponent from "./components/commercetools-ui/atoms/dropdown/custom-dropdown";
import DefaultDropdownComponent from "./components/commercetools-ui/atoms/dropdown/default-dropdown";
import type { DropdownProps as SharedDropdownProps } from "./components/commercetools-ui/atoms/dropdown/default-dropdown";
import RadioComponent from "./components/commercetools-ui/atoms/radio";
import CheckboxComponent from "./components/commercetools-ui/atoms/checkbox";
import ApprovalRequiredNoticeComponent from "./components/commercetools-ui/atoms/approval-required-notice";
import QuantityNumberInputComponent from "./components/commercetools-ui/atoms/quantity-number-input";
import type { ButtonProps as SharedButtonProps } from "./components/commercetools-ui/atoms/button";
import type { CheckboxProps as SharedCheckboxProps } from "./components/commercetools-ui/atoms/checkbox";
import type { RadioProps as SharedRadioProps } from "./components/commercetools-ui/atoms/radio";
import type { QuantityNumberInputProps as SharedQuantityNumberInputProps } from "./components/commercetools-ui/atoms/quantity-number-input";
import type { ApprovalRequiredNoticeProps as SharedApprovalRequiredNoticeProps } from "./components/commercetools-ui/atoms/approval-required-notice";
import type { InfoProps as SharedInfoProps } from "./components/commercetools-ui/atoms/info";
import type { SideMenuProps as SharedSideMenuProps } from "./components/commercetools-ui/atoms/side-menu";
import type { CustomTooltipProps as SharedCustomTooltipProps } from "./components/commercetools-ui/atoms/tooltip";
import type { OverlayProps as SharedOverlayProps } from "./components/commercetools-ui/atoms/overlay";
import type { SubtitleProps as SharedSubtitleProps } from "./components/commercetools-ui/atoms/subtitle";
import type { BreadcrumbProps as SharedBreadcrumbProps } from "./components/commercetools-ui/atoms/breadcrumb";
import type { SVGIconProps as SharedSVGIconProps } from "./components/commercetools-ui/atoms/svg-icon";
import type { CustomDropdownProps as SharedCustomDropdownProps } from "./components/commercetools-ui/atoms/dropdown/custom-dropdown";

// New Atoms
import AccordionComponent from "./components/commercetools-ui/atoms/accordion";
import DrawerComponent from "./components/commercetools-ui/atoms/drawer";
import ModalComponent from "./components/commercetools-ui/atoms/modal";
import SliderComponent from "./components/commercetools-ui/atoms/slider";
import SelectComponent from "./components/commercetools-ui/atoms/select";
import InputComponent from "./components/commercetools-ui/atoms/input";
import PasswordInputComponent from "./components/commercetools-ui/atoms/input-password";
import LinkComponent from "./components/commercetools-ui/atoms/link";
import TypographyComponent from "./components/commercetools-ui/atoms/typography";
import ImageGalleryComponent from "./components/commercetools-ui/atoms/image-gallery";
import OutOfStockComponent from "./components/commercetools-ui/atoms/out-of-stock";
import CustomSelectionComponent from "./components/commercetools-ui/atoms/custom-selection";
import SearchComponent from "./components/commercetools-ui/atoms/search";
import SearchAddressComponent from "./components/commercetools-ui/atoms/search-address";
import SearchAlgoliaComponent from "./components/commercetools-ui/atoms/search-algolia";
import ContentfulRichTextComponent from "./components/commercetools-ui/atoms/contentful-rich-text";
import SanityRichTextComponent from "./components/commercetools-ui/atoms/sanity-rich-text";
import type { AccordionProps as SharedAccordionProps } from "./components/commercetools-ui/atoms/accordion";
import type { DrawerProps as SharedDrawerProps } from "./components/commercetools-ui/atoms/drawer";
import type { InputProps as SharedInputProps } from "./components/commercetools-ui/atoms/input";
import type { PasswordInputProps as SharedPasswordInputProps } from "./components/commercetools-ui/atoms/input-password";
import type { LinkProps as SharedLinkProps } from "./components/commercetools-ui/atoms/link";
import type { TypographyProps as SharedTypographyProps } from "./components/commercetools-ui/atoms/typography/types";
import type { SliderProps as SharedSliderProps } from "./components/commercetools-ui/atoms/slider";

// Atom hooks / utils
import useClassNames from "./hooks/useClassNames";
import useMediaQuery from "./hooks/useMediaQuery";
import {
    smallMobile,
    mobile,
    tablet,
    desktop,
    mediumDesktop,
    largeDesktop,
    hugeDesktop,
} from "./utils/screensizes";

// SSO Components
import { SSOLogin, SSOErrorPage } from "./components/sso";

// Utils
import {
    getProductAvailability,
    isProductPurchasable,
    shouldNotifyStock,
} from "./utils/productAvailability";
import { buildUpsTrackingHref } from "./utils/upsTrackingUrl";

// SSO Utils
import {
    isSSOEnabled,
    performLogout,
    clearServiceWorkers,
} from "./utils/sso";

import {
    createKeycloakProvider,
    createTokenRefreshFunction,
    createNextAuthCallbacks,
    createNextAuthConfig,
    createLogoutApiHandler,
    parseJWTUserInfo,
} from "./utils/sso/nextauth-helpers";

import {
    lookupSSOUser,
    getUserTypeDescription,
    extractEmployeeId,
    isStoreAccount,
} from "./utils/sso/user-lookup";

// Hooks and Cart utilities
import { useCartShared, getOrderByCartId } from "./hooks/useCartShared";
import { useSSO } from "./hooks/useSSO";
import { loadUserConfig, DEFAULT_USER_CONFIG } from "./hooks/useUserConfig";
export type { UserConfig } from "./hooks/useUserConfig";
import {
    loadProductConfig,
    loadProductConfigFromStorage,
    loadProductTileFeaturesFromStorage,
    loadShowSecondImageOnHoverFromStorage,
    DEFAULT_CARD_TYPE,
    DEFAULT_PRODUCT_TILE_FEATURES,
    DEFAULT_SHOW_SECOND_IMAGE_ON_HOVER,
    ProductCardType,
    initializeProductConfig,
    getProductCardType,
    getProductCardTypeOrNull,
    getProductTileFeatures,
    getProductTileFeaturesOrNull,
    getShowSecondImageOnHover,
    subscribeToProductTileFeatures,
    useProductVariantConfig,
    isCompactCardType,
} from "./hooks/useProductConfig";
export type {
    ProductConfig,
    ProductTileFeatures,
    ProductVariantConfig,
    ProductVariantLook,
} from "./hooks/useProductConfig";
import { compareSizes } from "./helpers/sizeOrder";
import {
    buildDefaultPlpFacetsConfiguration,
    buildPlpFacetsFromCommercetoolsResponse,
    PLP_FACET_ORDER,
} from "./helpers/plpFacets";
import {
    getColorSwatchSurfaceProps,
    getFacetColorSwatchClassName,
} from "./helpers/facetColorSwatch";
import {
    buildColorOptionsFromProducts,
    orderFacetTermsByColorOptions,
    resolveColorOptionForFacetTerm,
} from "./helpers/colorFacetHelpers";
import ProductSortControl from "./components/product-sort-control";
import {
    DEFAULT_PRODUCT_SORT,
    PRODUCT_SORT_OPTION_DEFINITIONS,
    PRODUCT_SORT_OPTIONS,
    buildProductQueryParams,
    isProductSortOption,
} from "./helpers/product/ProductSort";

// Initialization functions
import { initializeProductAvailabilityConfig } from "./hooks/useProductAvailabilityConfig";


// Keep React types from the shared package out of app-level JSX checks.
const Toaster = ToasterComponent as () => any;
const Skeleton = SkeletonComponent as () => any;
const PDFDisplay = PDFDisplayComponent as (props: any) => any;
const SVGIcon = SVGIconComponent as (props: SharedSVGIconProps) => any;
const Breadcrumb = BreadcrumbComponent as (props: SharedBreadcrumbProps) => any;
const Button = ButtonComponent as (props: SharedButtonProps) => any;
const CustomTooltip = CustomTooltipComponent as (props: SharedCustomTooltipProps) => any;
const Subtitle = SubtitleComponent as (props: SharedSubtitleProps) => any;
const Overlay = OverlayComponent as (props: SharedOverlayProps) => any;
const Info = InfoComponent as (props: SharedInfoProps) => any;
const SideMenu = SideMenuComponent as (props: SharedSideMenuProps) => any;
const CustomDropDown = CustomDropDownComponent as (props: SharedCustomDropdownProps) => any;
const DefaultDropdown = DefaultDropdownComponent as (props: SharedDropdownProps) => any;
const Radio = RadioComponent as (props: SharedRadioProps) => any;
const Checkbox = CheckboxComponent as (props: SharedCheckboxProps) => any;
const ApprovalRequiredNotice = ApprovalRequiredNoticeComponent as (props: SharedApprovalRequiredNoticeProps) => any;
const QuantityNumberInput = QuantityNumberInputComponent as (props: SharedQuantityNumberInputProps) => any;

// New atoms - type cast to avoid cross-workspace React type conflicts
const Accordion = AccordionComponent as any;
const Drawer = DrawerComponent as (props: SharedDrawerProps) => any;
const Modal = ModalComponent as any;
const Slider = SliderComponent as (props: SharedSliderProps) => any;
const Select = SelectComponent as any;
const Input = InputComponent as (props: SharedInputProps) => any;
const PasswordInput = PasswordInputComponent as (props: SharedPasswordInputProps) => any;
const Link = LinkComponent as (props: SharedLinkProps) => any;
const Typography = TypographyComponent as (props: SharedTypographyProps) => any;
const ImageGallery = ImageGalleryComponent as any;
const OutOfStock = OutOfStockComponent as any;
const CustomSelection = CustomSelectionComponent as any;
const Search = SearchComponent as any;
const SearchAddress = SearchAddressComponent as any;
const SearchAlgolia = SearchAlgoliaComponent as any;
const ContentfulRichText = ContentfulRichTextComponent as any;
const SanityRichText = SanityRichTextComponent as any;

// Keep ReactNode/FC types from the shared package out of app-level JSX checks.
const UpsTrackingLink = UpsTrackingLinkComponent as (props: UpsTrackingLinkProps) => any;
const SidebarSelectedFilters = SidebarSelectedFiltersComponent as (props: SidebarSelectedFiltersProps) => any;

// Types
export type {
    ProductAvailabilityTag,
    ProductAvailabilityTagsProps,
    AvailabilityTagConfig,
} from "./components/product-availability/types";
export type { UpsTrackingLinkProps } from "./components/commercetools-ui/organisms/account/sections/order-detail-page/ups-tracking-link";
export type { ProductAvailability } from "./utils/productAvailability";
export type { ColorFacetSwatchProps } from "./components/product-color-swatches/ColorFacetSwatch";
export type {
    SelectedFilterItem,
    SelectedFilterVariant,
    SidebarSelectedFiltersProps,
} from "./components/sidebar-selected-filters/SidebarSelectedFilters";
export type {
    ProductColorOption,
    ProductColorSwatchesProps,
} from "./components/product-color-swatches/types";
export type { ProductSortControlProps } from "./components/product-sort-control";
export type { ProductSortOption, ProductSortOptionDefinition } from "./helpers/product/ProductSort";

// SSO Types
export type { SSOConfig, ExtendedJWT, ExtendedSession } from "./utils/sso/nextauth-helpers";
export type { SSOUserInfo } from "./utils/sso/user-lookup";

// Atom types
export type { BreadcrumbProps } from "./components/commercetools-ui/atoms/breadcrumb";
export type { SVGIconProps } from "./components/commercetools-ui/atoms/svg-icon";
export type { PDFDisplayProps } from "./components/commercetools-ui/atoms/pdf-viewer";
export type {
    ButtonProps,
    ButtonVariant,
    ButtonSize,
    ButtonIconPosition,
} from "./components/commercetools-ui/atoms/button";
export type { FeedbackIconLayerProps } from "./components/commercetools-ui/atoms/button/feedbackIconLayer";
export type { LoadingIconProps } from "./components/commercetools-ui/atoms/button/loadingIcon";
export type { CustomTooltipProps } from "./components/commercetools-ui/atoms/tooltip";
export type { SubtitleProps } from "./components/commercetools-ui/atoms/subtitle";
export type { OverlayProps } from "./components/commercetools-ui/atoms/overlay";
export type { InfoProps } from "./components/commercetools-ui/atoms/info";
export type { SideMenuProps, SideMenuItem } from "./components/commercetools-ui/atoms/side-menu";
export type { CustomDropdownProps } from "./components/commercetools-ui/atoms/dropdown/custom-dropdown";
export type { DropdownProps } from "./components/commercetools-ui/atoms/dropdown/default-dropdown";
export type { RadioProps } from "./components/commercetools-ui/atoms/radio";
export type { CheckboxProps } from "./components/commercetools-ui/atoms/checkbox";
export type { ApprovalRequiredNoticeProps } from "./components/commercetools-ui/atoms/approval-required-notice";
export type { QuantityNumberInputProps } from "./components/commercetools-ui/atoms/quantity-number-input";
export type { AccordionProps } from "./components/commercetools-ui/atoms/accordion";
export type { DrawerProps } from "./components/commercetools-ui/atoms/drawer";
export type { InputProps } from "./components/commercetools-ui/atoms/input";
export type { PasswordInputProps } from "./components/commercetools-ui/atoms/input-password";
export type { LinkProps, LinkVariant } from "./components/commercetools-ui/atoms/link";
export type { TypographyProps } from "./components/commercetools-ui/atoms/typography/types";
export type { SliderProps } from "./components/commercetools-ui/atoms/slider";
export type { Option as SelectOption } from "./components/commercetools-ui/atoms/select";

export {
    // Product Availability
    TextTitle,
    ProductAvailabilityTags,

    // Atoms
    Toaster,
    Skeleton,
    PDFDisplay,
    SVGIcon,
    Breadcrumb,
    Button,
    ButtonLoadingIcon,
    ButtonFeedbackIconLayer,
    CustomTooltip,
    Subtitle,
    Overlay,
    Info,
    SideMenu,
    CustomDropDown,
    DefaultDropdown,
    Radio,
    Checkbox,
    ApprovalRequiredNotice,
    QuantityNumberInput,
    Accordion,
    Drawer,
    Modal,
    Slider,
    Select,
    Input,
    PasswordInput,
    Link,
    Typography,
    ImageGallery,
    OutOfStock,
    CustomSelection,
    Search,
    SearchAddress,
    SearchAlgolia,
    ContentfulRichText,
    SanityRichText,

    // Atom hooks / utils
    useClassNames,
    useMediaQuery,
    smallMobile,
    mobile,
    tablet,
    desktop,
    mediumDesktop,
    largeDesktop,
    hugeDesktop,

    // Product Color Swatches
    ProductColorSwatches,
    ColorFacetSwatch,
    SidebarSelectedFilters,
    getProductColorOptions,

    UpsTrackingLink,
    getProductAvailability,
    isProductPurchasable,
    shouldNotifyStock,
    buildUpsTrackingHref,
    initializeProductAvailabilityConfig,

    // Cart (Deprecated - use getOrderByCartId directly)
    useCartShared,
    getOrderByCartId,

    // SSO Components
    SSOLogin,
    SSOErrorPage,

    // SSO Utils
    isSSOEnabled,
    performLogout,
    clearServiceWorkers,

    // SSO NextAuth Helpers
    createKeycloakProvider,
    createTokenRefreshFunction,
    createNextAuthCallbacks,
    createNextAuthConfig,
    createLogoutApiHandler,
    parseJWTUserInfo,

    // SSO User Lookup
    lookupSSOUser,
    getUserTypeDescription,
    extractEmployeeId,
    isStoreAccount,

    // SSO Hooks
    useSSO,

    // User Config
    loadUserConfig,
    DEFAULT_USER_CONFIG,

    // Product Config
    loadProductConfig,
    loadProductConfigFromStorage,
    loadProductTileFeaturesFromStorage,
    loadShowSecondImageOnHoverFromStorage,
    initializeProductConfig,
    getProductCardType,
    getProductCardTypeOrNull,
    getProductTileFeatures,
    getProductTileFeaturesOrNull,
    getShowSecondImageOnHover,
    subscribeToProductTileFeatures,
    useProductVariantConfig,
    compareSizes,
    isCompactCardType,
    DEFAULT_CARD_TYPE,
    DEFAULT_PRODUCT_TILE_FEATURES,
    DEFAULT_SHOW_SECOND_IMAGE_ON_HOVER,
    ProductCardType,
    buildDefaultPlpFacetsConfiguration,
    buildPlpFacetsFromCommercetoolsResponse,
    PLP_FACET_ORDER,
    getFacetColorSwatchClassName,
    getColorSwatchSurfaceProps,
    buildColorOptionsFromProducts,
    orderFacetTermsByColorOptions,
    resolveColorOptionForFacetTerm,

    // Product Sort
    ProductSortControl,
    DEFAULT_PRODUCT_SORT,
    PRODUCT_SORT_OPTIONS,
    PRODUCT_SORT_OPTION_DEFINITIONS,
    buildProductQueryParams,
    isProductSortOption,
};

// ---------------------------------------------------------------------------
// Shared helpers, hooks, utils and constants migrated out of the per-app
// `helpers/*` trees. The apps import these directly from
// `@loomstateorg/shared-frontend/...`; no local copies remain.
// ---------------------------------------------------------------------------

// Helpers
export * from "./helpers/arrayHelpers";
export * from "./helpers/currencyHelpers";
export * from "./helpers/dateHelpers";
export * from "./helpers/errorLogger";
export * from "./helpers/facetSortOrder";
export * from "./helpers/formatter";
export * from "./helpers/reference";
export * from "./helpers/stringHelpers";
export * from "./helpers/domSanitizer";
export { default as Redirect } from "./helpers/redirect";
export type { RedirectProps } from "./helpers/redirect";

// Constants
export * from "./constants/algolia";
export * from "./constants/auth";
export * from "./constants/events";
export * from "./constants/googleAnalytics";
export * from "./constants/localStorage";

// Utils
export * from "./utils/breakpoints";
export * from "./utils/environment";
export * from "./utils/formatLocaleName";
export * from "./utils/getScrollbarWidth";
export * from "./utils/guid";
export * from "./utils/i18n";
export { default as getFontSize } from "./utils/getFontSize";

// Hooks
export * from "./hooks/useFormat";
export { default as useCloseFlyouts } from "./hooks/useCloseFlyouts";
export { default as useCurrentBreakpoint } from "./hooks/useCurrentBreakpoint";
export { default as useDates } from "./hooks/useDates";
export { default as useHash } from "./hooks/useHash";
export { default as useImageSizes } from "./hooks/useImageSizes";
export type { UseImageSizesParams } from "./hooks/useImageSizes";
export { default as useOnClickOutside } from "./hooks/useOnClickOutside";
export { default as usePreloadImages } from "./hooks/usePreloadImages";
export { default as useProcessing } from "./hooks/useProcessing";
export { default as useResizeObserver } from "./hooks/useResizeObserver";
export { default as useScrollBlock } from "./hooks/useScrollBlock";
export { default as useScrollDirection } from "./hooks/useScrollDirection";
export { default as useTouchDevice } from "./hooks/useTouchDevice";
export { default as useValidate } from "./hooks/useValidate";
export { default as useVariantWithDiscount } from "./hooks/useVariantWithDiscount";
export { default as useViewportWidth } from "./hooks/useViewportWidth";
export { default as useWindowLoaded } from "./hooks/useWindowLoaded";
